import { useQuery } from "@tanstack/react-query";

import { useAuth } from "@/providers";
import { useDomainServices } from "@/providers/DomainServices";
import type { TopicService } from "@/shared/domain/topics/TopicService";

/**
 * The distinct field paths of a single topic, resolved by name through the
 * shared-topics/fields schema. Disabled until both an org and a non-empty
 * `topicName` are available, so callers can pass `null` while the user hasn't
 * settled on a topic yet. Cached per `(orgId, topicName)`.
 */
export default function useTopicFields(
  topicName: string | null,
  orgId?: string,
) {
  const { currentOrganization } = useAuth();
  const canonicalOrgId = orgId || currentOrganization?.org_id;
  const { topics: topicService } = useDomainServices();

  return useQuery<string[], Error>({
    ...topicFieldsQuery(topicService, canonicalOrgId ?? "", topicName ?? ""),
    enabled: canonicalOrgId !== undefined && !!topicName,
  });
}

/**
 * The query `useTopicFields` runs for the field paths of the topic
 * `topicName` in `orgId`, for a caller that lists several topics' fields or
 * fetches outside React (`queryClient.fetchQuery`) through the same cache
 * entry.
 */
export function topicFieldsQuery(
  topicService: TopicService,
  orgId: string,
  topicName: string,
) {
  return {
    queryKey: ["topicFields", orgId, topicName],
    queryFn: ({ signal }: { signal: AbortSignal }) =>
      topicService.getTopicFields(topicName, {
        abortSignal: signal,
        resourceOwnerId: orgId,
      }),
    staleTime: 30_000,
  };
}
